import { useCallback, useMemo } from 'react';

import useAPI, { type State } from '~/common/hooks/useAPI';
import getIssueTypeFields, {
  type AllowedValue,
  type IssueTypeField
} from '~/common/utils/integrations-client/jira/getIssueTypeFields';

export interface IssueTypeFieldsState extends State<IssueTypeField[]> {
  priorities: IssueTypeField | null;
}

export interface IssueTypeFieldsMethods {
  getPriorityById: (id: string) => AllowedValue | null;
}

const useIssueTypeFields = (
  enterpriseId: string,
  projectId: string,
  issueTypeId: string,
  connectionId?: string,
  token?: string,
  skipRequest = false
): IssueTypeFieldsState & IssueTypeFieldsMethods => {
  const dataLoader = useCallback(() => {
    if (!connectionId) {
      throw new Error('Connection ID not found');
    }

    return getIssueTypeFields({
      enterpriseId,
      connectionId,
      projectId,
      issueTypeId,
      token
    });
  }, [projectId, issueTypeId, token, enterpriseId]);

  const state = useAPI(dataLoader, skipRequest);

  const priorities = useMemo(() => {
    return state.data?.filter(field => field.key === 'priority')?.[0] || null;
  }, [state.data]);

  const getPriorityById = useCallback(
    (priorityValueId: string) =>
      priorities?.allowedValues?.find(({ id }) => id === priorityValueId) ||
      null,
    [priorities]
  );

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    priorities,
    getPriorityById
  };
};

export default useIssueTypeFields;
